import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import Button from '@components/common/Button/Button'
import Input from '@components/common/Input/Input'
import { isRequired, minLength } from '@utils/validators'
import authService from '@services/authService'
import './ResetPassword.css'

/**
 * Reset Password Page Component
 *
 * Allows users to set a new password using a valid reset token.
 *
 * SECURITY FEATURES:
 * - Token validation before allowing password reset
 * - Password strength requirements
 * - Password confirmation matching
 * - Single-use token (invalidated after use)
 *
 * @component
 */
const ResetPassword = () => {
	const { token } = useParams()
	const navigate = useNavigate()

	// Token validation state
	const [isValidatingToken, setIsValidatingToken] = useState(true)
	const [isTokenValid, setIsTokenValid] = useState(false)
	const [maskedEmail, setMaskedEmail] = useState('')

	// Form state
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [isSuccess, setIsSuccess] = useState(false)

	// Validation state
	const [errors, setErrors] = useState({
		password: '',
		confirmPassword: ''
	})
	const [touched, setTouched] = useState({
		password: false,
		confirmPassword: false
	})

	/**
	 * Validate token on mount
	 */
	useEffect(() => {
		const validateToken = async () => {
			if (!token) {
				setIsTokenValid(false)
				setIsValidatingToken(false)
				return
			}

			try {
				const response = await authService.verifyResetToken(token)
				setIsTokenValid(true)
				setMaskedEmail(response.data?.email || '')
			} catch (error) {
				console.error('Token validation error:', error)
				setIsTokenValid(false)
			} finally {
				setIsValidatingToken(false)
			}
		}

		validateToken()
	}, [token])

	/**
	 * Validate password field
	 */
	const validatePasswordField = (value) => {
		if (!isRequired(value)) {
			return 'Password is required'
		}
		if (!minLength(value, 8)) {
			return 'Password must be at least 8 characters'
		}
		return ''
	}

	/**
	 * Validate confirm password field
	 */
	const validateConfirmPasswordField = (value) => {
		if (!isRequired(value)) {
			return 'Please confirm your password'
		}
		if (value !== password) {
			return 'Passwords do not match'
		}
		return ''
	}

	/**
	 * Validate entire form
	 */
	const validateForm = () => {
		const passwordError = validatePasswordField(password)
		const confirmPasswordError = validateConfirmPasswordField(confirmPassword)

		setErrors({
			password: passwordError,
			confirmPassword: confirmPasswordError
		})

		return !passwordError && !confirmPasswordError
	}

	/**
	 * Handle password input change
	 */
	const handlePasswordChange = (e) => {
		const value = e.target.value
		setPassword(value)

		if (touched.password) {
			setErrors(prev => ({
				...prev,
				password: validatePasswordField(value)
			}))
		}

		// Re-validate confirm password if it was touched
		if (touched.confirmPassword && confirmPassword) {
			setErrors(prev => ({
				...prev,
				confirmPassword: value !== confirmPassword ? 'Passwords do not match' : ''
			}))
		}
	}

	/**
	 * Handle confirm password input change
	 */
	const handleConfirmPasswordChange = (e) => {
		const value = e.target.value
		setConfirmPassword(value)

		if (touched.confirmPassword) {
			setErrors(prev => ({
				...prev,
				confirmPassword: validateConfirmPasswordField(value)
			}))
		}
	}

	/**
	 * Handle field blur
	 */
	const handleBlur = (field) => {
		setTouched(prev => ({ ...prev, [field]: true }))

		if (field === 'password') {
			setErrors(prev => ({
				...prev,
				password: validatePasswordField(password)
			}))
		} else if (field === 'confirmPassword') {
			setErrors(prev => ({
				...prev,
				confirmPassword: validateConfirmPasswordField(confirmPassword)
			}))
		}
	}

	/**
	 * Handle form submission
	 */
	const handleSubmit = async (e) => {
		e.preventDefault()

		// Mark all fields as touched
		setTouched({ password: true, confirmPassword: true })

		// Validate form
		if (!validateForm()) {
			return
		}

		setIsLoading(true)

		try {
			await authService.resetPassword(token, password, confirmPassword)

			setIsSuccess(true)
			toast.success('Password reset successfully!')

			// Redirect to login after 3 seconds
			setTimeout(() => {
				navigate('/login', { replace: true })
			}, 3000)
		} catch (error) {
			console.error('Reset password error:', error)

			if (error.message?.includes('expired') || error.message?.includes('invalid')) {
				setIsTokenValid(false)
			} else {
				toast.error(error.message || 'Failed to reset password. Please try again.')
			}
		} finally {
			setIsLoading(false)
		}
	}

	/**
	 * Calculate password strength
	 */
	const getPasswordStrength = () => {
		if (!password) return { score: 0, label: '', color: '' }

		let score = 0
		if (password.length >= 8) score++
		if (password.length >= 12) score++
		if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
		if (/[0-9]/.test(password)) score++
		if (/[^a-zA-Z0-9]/.test(password)) score++

		if (score <= 1) return { score: 1, label: 'Weak', color: '#ef4444' }
		if (score <= 2) return { score: 2, label: 'Fair', color: '#f59e0b' }
		if (score <= 3) return { score: 3, label: 'Good', color: '#22c55e' }
		return { score: 4, label: 'Strong', color: '#10b981' }
	}

	const passwordStrength = getPasswordStrength()

	// Loading state - validating token
	if (isValidatingToken) {
		return (
			<div className="reset-password-page">
				<div className="reset-password-container">
					<div className="reset-password-card">
						<div className="loading-state">
							<div className="loading-spinner"></div>
							<p>Validating your reset link...</p>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Invalid token state
	if (!isTokenValid) {
		return (
			<div className="reset-password-page">
				<div className="reset-password-container">
					<div className="reset-password-card">
						{/* Error Icon */}
						<div className="error-icon">
							<i className="icon-x-circle" aria-hidden="true"></i>
						</div>

						{/* Error Message */}
						<div className="reset-password-header">
							<h1 className="reset-password-title">Invalid or Expired Link</h1>
							<p className="reset-password-subtitle">
								This password reset link is no longer valid. It may have expired
								or already been used.
							</p>
						</div>

						{/* Actions */}
						<div className="reset-password-actions">
							<Link to="/forgot-password">
								<Button variant="primary" size="large" className="action-button">
									Request New Link
								</Button>
							</Link>

							<Link to="/login" className="back-to-login-link">
								<i className="icon-arrow-left" aria-hidden="true"></i>
								Back to Login
							</Link>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Success state
	if (isSuccess) {
		return (
			<div className="reset-password-page">
				<div className="reset-password-container">
					<div className="reset-password-card">
						{/* Success Icon */}
						<div className="success-icon">
							<i className="icon-check-circle" aria-hidden="true"></i>
						</div>

						{/* Success Message */}
						<div className="reset-password-header">
							<h1 className="reset-password-title">Password Reset!</h1>
							<p className="reset-password-subtitle">
								Your password has been successfully reset. You can now log in
								with your new password.
							</p>
						</div>

						{/* Info */}
						<div className="info-box">
							<i className="icon-info" aria-hidden="true"></i>
							<p>Redirecting to login page in a few seconds...</p>
						</div>

						{/* Actions */}
						<div className="reset-password-actions">
							<Link to="/login">
								<Button variant="primary" size="large" className="action-button">
									Go to Login
								</Button>
							</Link>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Default state - reset form
	return (
		<div className="reset-password-page">
			<div className="reset-password-container">
				<div className="reset-password-card">
					{/* Header */}
					<div className="reset-password-header">
						{/* Logo */}
						<div className="reset-password-logo">
							<div className="logo-placeholder">
								<i className="icon-lock" aria-hidden="true"></i>
							</div>
						</div>

						{/* Title */}
						<h1 className="reset-password-title">Create New Password</h1>
						<p className="reset-password-subtitle">
							{maskedEmail && (
								<>Enter a new password for <strong>{maskedEmail}</strong></>
							)}
							{!maskedEmail && 'Enter your new password below.'}
						</p>
					</div>

					{/* Form */}
					<form className="reset-password-form" onSubmit={handleSubmit} noValidate>
						{/* New Password Input */}
						<div className="password-input-wrapper">
							<Input
								type={showPassword ? 'text' : 'password'}
								id="new-password"
								label="New Password"
								placeholder="Enter new password"
								value={password}
								onChange={handlePasswordChange}
								onBlur={() => handleBlur('password')}
								error={touched.password ? errors.password : ''}
								required
								disabled={isLoading}
								autoComplete="new-password"
								autoFocus
							/>
							<button
								type="button"
								className="password-toggle"
								onClick={() => setShowPassword(!showPassword)}
								disabled={isLoading}
								aria-label={showPassword ? 'Hide password' : 'Show password'}
							>
								<i className={showPassword ? 'icon-eye-off' : 'icon-eye'} aria-hidden="true"></i>
							</button>
						</div>

						{/* Password Strength Indicator */}
						{password && (
							<div className="password-strength">
								<div className="strength-bar">
									<div
										className="strength-fill"
										style={{
											width: `${(passwordStrength.score / 4) * 100}%`,
											backgroundColor: passwordStrength.color
										}}
									></div>
								</div>
								<span className="strength-label" style={{ color: passwordStrength.color }}>
									{passwordStrength.label}
								</span>
							</div>
						)}

						{/* Confirm Password Input */}
						<div className="password-input-wrapper">
							<Input
								type={showConfirmPassword ? 'text' : 'password'}
								id="confirm-password"
								label="Confirm Password"
								placeholder="Confirm new password"
								value={confirmPassword}
								onChange={handleConfirmPasswordChange}
								onBlur={() => handleBlur('confirmPassword')}
								error={touched.confirmPassword ? errors.confirmPassword : ''}
								required
								disabled={isLoading}
								autoComplete="new-password"
							/>
							<button
								type="button"
								className="password-toggle"
								onClick={() => setShowConfirmPassword(!showConfirmPassword)}
								disabled={isLoading}
								aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
							>
								<i className={showConfirmPassword ? 'icon-eye-off' : 'icon-eye'} aria-hidden="true"></i>
							</button>
						</div>

						{/* Password Requirements */}
						<div className="password-requirements">
							<p className="requirements-title">Password must:</p>
							<ul>
								<li className={password.length >= 8 ? 'met' : ''}>
									<i className={password.length >= 8 ? 'icon-check' : 'icon-circle'} aria-hidden="true"></i>
									Be at least 8 characters
								</li>
								<li className={/[A-Z]/.test(password) && /[a-z]/.test(password) ? 'met' : ''}>
									<i className={/[A-Z]/.test(password) && /[a-z]/.test(password) ? 'icon-check' : 'icon-circle'} aria-hidden="true"></i>
									Include upper and lowercase letters
								</li>
								<li className={/[0-9]/.test(password) ? 'met' : ''}>
									<i className={/[0-9]/.test(password) ? 'icon-check' : 'icon-circle'} aria-hidden="true"></i>
									Include at least one number
								</li>
							</ul>
						</div>

						{/* Submit Button */}
						<Button
							type="submit"
							variant="primary"
							size="large"
							loading={isLoading}
							disabled={isLoading}
							className="reset-password-button"
						>
							{isLoading ? 'Resetting...' : 'Reset Password'}
						</Button>
					</form>

					{/* Footer */}
					<div className="reset-password-footer">
						<Link to="/login" className="back-to-login-link">
							<i className="icon-arrow-left" aria-hidden="true"></i>
							Back to Login
						</Link>
					</div>
				</div>
			</div>
		</div>
	)
}

export default ResetPassword
